/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, NodeTag } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { InteractiveFeatureCollectionLinks } from './components';
import { IHeadingData, IInteractiveFeatureItemProps } from './interactive-feature-item.props.autogenerated';

/**
 * Interactive Feature Item view props.
 */
export interface IInteractiveFeatureItemViewProps extends IInteractiveFeatureItemProps<{}> {
    title: React.ReactNode;
    paragraph: React.ReactNode;
    image: React.ReactNode;
    links: React.ReactNode;
    featureItem: IModuleProps;
    isMosaic: boolean;
    indexItem?: number;
    itemCounter?: number;

    interactiveFeatureCollectionCarouselContainer: INodeProps;
    interactiveFeatureCollectionCarouselImage: INodeProps;
    interactiveFeatureCollectionCarouselContent: INodeProps;

    interactiveFeatureCollectionMosaicContainer: INodeProps;
    interactiveFeatureCollectionMosaicImage: INodeProps;
    interactiveFeatureCollectionMosaicDetails: INodeProps;
}

/**
 * Interactive Feature Item Extented props.
 */
export interface IInteractiveFeatureItemExtentedProps extends IInteractiveFeatureItemProps<{}> {
    isMosaic?: boolean;
    indexItem?: number;
    itemCounter?: number;
}

/**
 *
 * InteractiveFeatureItem component.
 * @extends {React.PureComponent<IInteractiveFeatureItemExtentedProps>}
 */
export class InteractiveFeatureItem extends React.PureComponent<IInteractiveFeatureItemExtentedProps> {
    private readonly _telemetryContent: ITelemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry);

    public constructor(props: IInteractiveFeatureItemProps<{}>) {
        super(props);
    }

    public render(): JSX.Element | null {
        const { indexItem, itemCounter, isMosaic } = this.props;
        const { heading, paragraph, image, links } = this.props.config;
        const featureItemLinks = links && ArrayExtensions.hasElements(links) && (
            <InteractiveFeatureCollectionLinks
                links={links}
                onTextChange={this.handleLinkTextChange}
                requestContext={this.props.context.request}
                telemetryContent={this._telemetryContent}
            />
        );

        const viewProps = {
            ...this.props,
            indexItem,
            itemCounter,
            isMosaic: isMosaic ? isMosaic : false,
            title: this._createHeading(heading),
            paragraph: this._createParagraph(paragraph),
            image: this._renderImage(image),
            links: featureItemLinks,
            featureItem: {
                moduleProps: this.props,
                className: ''
            },
            interactiveFeatureCollectionCarouselContainer: {
                tag: 'div' as NodeTag,
                className: 'ms-interactive-feature-collection-carousel__container'
            },
            interactiveFeatureCollectionCarouselImage: {
                tag: 'div' as NodeTag,
                className: 'ms-interactive-feature-collection-carousel__image'
            },
            interactiveFeatureCollectionCarouselContent: {
                tag: 'div' as NodeTag,
                className: 'ms-interactive-feature-collection-carousel__heading'
            },
            interactiveFeatureCollectionMosaicContainer: {
                tag: 'div' as NodeTag,
                className: 'ms-interactive-feature-collection-mosaic',
                'aria-label': heading?.text,
                'tabindex': '0',
                'role': 'group'
            },
            interactiveFeatureCollectionMosaicImage: {
                className: 'ms-interactive-feature-collection-mosaic__image'
            },
            interactiveFeatureCollectionMosaicDetails: {
                className: 'ms-interactive-feature-collection-mosaic__details'
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handles heading content change event.
     * @param event - Content editable event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handles paragraph content change event.
     * @param event - Content editable event.
     */
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.paragraph = event.target.value;
    };

    /**
     * Handles paragraph content change event.
     * @param linkIndex - Index of link in linkarray.
     * @returns Void.
     */
    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent): void => {
        if (this.props.config.links?.[Number(linkIndex)]) {
            this.props.config.links[Number(linkIndex)].linkText = event.target.value;
        }
    };

    private _createParagraph(text?: Msdyn365.RichText): React.ReactNode | null {
        if (!text) {
            return null;
        }
        return (
            <Msdyn365.RichTextComponent
                text={text}
                className='ms-interactive-feature-collection__text'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );
    }

    private _createHeading(heading?: IHeadingData): React.ReactNode | null {
        if (!heading) {
            return null;
        }
        if (StringExtensions.isNullOrWhitespace(heading.text)) {
            return null;
        }

        return (
            <Msdyn365.Text
                tag={heading.tag ?? 'h2'}
                text={heading.text}
                className='ms-interactive-feature-collection__title'
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );
    }

    private _renderImage(item?: Msdyn365.IImageData, isAriaHiddenAttribute?: boolean): React.ReactNode | null {
        if (!item || StringExtensions.isNullOrWhitespace(item.src)) {
            return null;
        }

        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=328&h=459&m=6', w: 328, h: 459 },
                sm: { q: 'w=348&h=487&m=6', w: 348, h: 487 },
                md: { q: 'w=650&h=366&m=6', w: 650, h: 366 },
                lg: { q: 'w=650&h=366&m=6', w: 650, h: 366 },
                xl: { q: 'w=650&h=366&m=6', w: 650, h: 366 }
            },
            lazyload: true
        };
        const verifiedImageSettings = (item.imageSettings?.viewports) ? item.imageSettings : defaultImageSettings;
        return (
            <Msdyn365.Image
                {...item}
                requestContext={this.props.context.request}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={verifiedImageSettings}
                loadFailureBehavior='default'
                aria-hidden={isAriaHiddenAttribute}
                editProps={{
                    key: this.props.config.image ?? {}, requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image'
                }}
            />
        );
    }
}

export default InteractiveFeatureItem;
